import React from 'react';
import styled from 'styled-components';

import { isNotNullish } from 'shared/utils/type-utils';
import { MobileAppFlair } from 'types/graphql';
import { useCarousel } from 'src/hooks/use-carousel';
import { ProductCard } from 'src/dispensary-admin/components/product-card';
import { Products } from './product-carousel.types';

const HERO_SCALE = 1.08;
const SLIDE_WIDTH = 184;
const INITIAL_SLIDE_INDEX = 1;

type ProductHeroCarouselProps = {
  products: Products;
  asMobileApp?: boolean;
  buttonFlair?: MobileAppFlair;
};

/** Renders a snap-scrolling hero carousel with a scaled center product card. */
export function ProductHeroCarousel({ products, asMobileApp, buttonFlair }: ProductHeroCarouselProps): JSX.Element {
  const visibleProducts = products.filter(isNotNullish);
  const { carouselRef, selectedIndex } = useCarousel({
    carouselOptions: {
      align: 'center',
      containScroll: 'trimSnaps',
      dragFree: false,
      draggable: visibleProducts.length > 1,
      loop: false,
      slidesToScroll: 1,
      startIndex: Math.min(INITIAL_SLIDE_INDEX, Math.max(visibleProducts.length - 1, 0)),
    },
  });

  return (
    <HeroViewport ref={carouselRef} data-testid='product-hero-carousel'>
      <HeroContainer>
        {visibleProducts.map((product, index) => (
          <HeroSlide
            key={product.id}
            data-testid='product-hero-slide'
            data-selected={index === selectedIndex}
            $isSelected={index === selectedIndex}
          >
            <ProductCard product={product} asMobileApp={asMobileApp} buttonFlair={buttonFlair} />
          </HeroSlide>
        ))}
      </HeroContainer>
    </HeroViewport>
  );
}

const HeroViewport = styled.div`
  margin-left: -16px;
  overflow: hidden;
  padding: 20px 0 58px;
  width: calc(100% + 32px);
`;

const HeroContainer = styled.div`
  display: flex;
  gap: 25px;
`;

const HeroSlide = styled.div<{ $isSelected: boolean }>`
  flex: 0 0 ${SLIDE_WIDTH}px;
  transform: scale(${({ $isSelected }) => ($isSelected ? HERO_SCALE : 1)});
  transform-origin: center center;
  transition: transform 0.2s ease;
  width: ${SLIDE_WIDTH}px;
`;
